import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';

import { RefreshButton } from '../../components/RefreshButton';
import { SortHeader } from '../../components/SortHeader';
import { useTableSort, sortRows } from '../../util/tableSort';

import {
  getCrashReport,
  listCrashReports,
  type CrashReportDetail,
  type CrashReportSummary,
} from '../../api/crashReports';
import { extractErrorMessage } from '../../api/errors';

type CrashSortKey =
  | 'captured_at'
  | 'tenant_name'
  | 'device_label'
  | 'app_version'
  | 'status'
  | 'operator_note';

/** Order rank for the Status column: New < Downloaded < Resolved. */
function statusRank(row: CrashReportSummary): number {
  if (row.resolved_at) return 2;
  if (row.downloaded_at) return 1;
  return 0;
}

/**
 * Platform-admin Crash Reports inbox (superadmin only). The
 * capture-app's local error boundary + window handlers persist crash
 * payloads on the device and drain them to `/devices/me/crash-reports`
 * on the next launch — this page is where 10NEWTONS sees them across
 * every tenant.
 *
 * Shows the full log by default — an opened row stays visible with
 * a "Downloaded" status chip rather than disappearing. The Unread
 * Only toggle is the inbox-style filter for triage sessions where
 * the operator only wants new arrivals.
 *
 * IMPORTANT: opening the detail dialog is a side-effecting GET on the
 * backend — first read flips `downloaded_at` and writes a
 * `crash_report_downloaded` row into the tenant's event log so the
 * tenant_admin's Dashboard message log shows "evaluation started".
 * Idempotent on re-open. The dialog's Download button is purely
 * client-side: it writes the stack trace + metadata to a local
 * .txt file via a Blob URL without re-fetching.
 */

function formatTs(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  );
}

function shortId(uuid: string | null): string {
  return uuid ? uuid.slice(0, 8) : '—';
}

function StatusChip({ row }: { row: CrashReportSummary }) {
  if (row.resolved_at) {
    return (
      <Chip
        size="small"
        label="Resolved"
        color="success"
        variant="outlined"
      />
    );
  }
  if (row.downloaded_at) {
    return <Chip size="small" label="Downloaded" color="info" />;
  }
  return <Chip size="small" label="New" color="warning" />;
}

export function CrashReports({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const [items, setItems] = useState<CrashReportSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  // Newest-first by default; every data column is click-to-sort.
  const sort = useTableSort<CrashSortKey>('captured_at', 'desc');
  // Default off — the inbox is the full chronological log of every
  // report. Opened rows stay visible (status chip flips to
  // "Downloaded") instead of vanishing. The toggle is here for the
  // triage workflow where the operator wants the new-arrivals queue.
  const [unreadOnly, setUnreadOnly] = useState(false);

  // Detail dialog state. We keep `summary` (the row that was clicked)
  // alongside the loaded `detail` so the header can render immediately
  // while the stack trace is still in flight.
  const [activeSummary, setActiveSummary] = useState<CrashReportSummary | null>(
    null,
  );
  const [activeDetail, setActiveDetail] = useState<CrashReportDetail | null>(
    null,
  );
  const [detailError, setDetailError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try {
      const rows = await listCrashReports({
        unread_only: unreadOnly,
        limit: 200,
      });
      setItems(rows);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load crash reports'));
    } finally {
      setRefreshing(false);
    }
  }, [unreadOnly]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const openDetail = useCallback(async (row: CrashReportSummary) => {
    setActiveSummary(row);
    setActiveDetail(null);
    setDetailError(null);
    try {
      const detail = await getCrashReport(row.id);
      setActiveDetail(detail);
    } catch (err: unknown) {
      setDetailError(extractErrorMessage(err, 'failed to load crash report'));
    }
  }, []);

  const closeDetail = useCallback(() => {
    setActiveSummary(null);
    setActiveDetail(null);
    setDetailError(null);
    // The detail-view side-effect on the backend (flip downloaded_at)
    // means the row's status changed — reload so the inbox reflects
    // it without the operator having to click Refresh.
    void reload();
  }, [reload]);

  const controls = (
    <Stack direction="row" spacing={2} alignItems="center">
      <FormControlLabel
        control={
          <Switch
            checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
          />
        }
        label="Unread Only"
      />
      <RefreshButton onClick={() => void reload()} refreshing={refreshing} />
    </Stack>
  );

  return (
    <Stack spacing={3}>
      {actionsSlot ? (
        createPortal(controls, actionsSlot)
      ) : (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
        >
          {!embedded && (
            <Stack>
              <Typography variant="h4">Crash Reports</Typography>
              <Typography variant="body2" color="text.secondary">
                Capture-app crashes uploaded across every tenant. Click a
                row to read the stack trace — the act of opening the
                detail marks the report as evaluated and tells the tenant
                admin we're on it.
              </Typography>
            </Stack>
          )}
          {controls}
        </Stack>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {items === null && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {items !== null && items.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ReportProblemIcon
            sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
          />
          <Typography variant="body1" color="text.secondary">
            {unreadOnly
              ? 'No unread crash reports. Nothing new to triage right now — flip the toggle off to see the full log.'
              : 'No crash reports on record. The capture-app uploads here when it catches a crash.'}
          </Typography>
        </Paper>
      )}

      {items !== null && items.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortHeader
                  label="Captured"
                  col="captured_at"
                  sort={sort}
                  sx={{ width: 160 }}
                />
                <SortHeader label="Tenant" col="tenant_name" sort={sort} />
                <SortHeader label="Device" col="device_label" sort={sort} />
                <SortHeader label="Version" col="app_version" sort={sort} />
                <SortHeader label="Status" col="status" sort={sort} />
                <SortHeader label="Note" col="operator_note" sort={sort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortRows(
                items,
                (row) => {
                  switch (sort.orderBy) {
                    case 'captured_at':
                      return row.captured_at;
                    case 'tenant_name':
                      return row.tenant_name ?? row.tenant_slug;
                    case 'device_label':
                      return row.device_label;
                    case 'app_version':
                      return row.app_version;
                    case 'status':
                      return statusRank(row);
                    case 'operator_note':
                      return row.operator_note;
                  }
                },
                sort.order,
              ).map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => void openDetail(row)}
                >
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {formatTs(row.captured_at)}
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {row.tenant_name ?? shortId(row.tenant_id)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.tenant_slug ?? row.tenant_id.slice(0, 8)}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack>
                      <Typography variant="body2">
                        {row.device_label ?? (
                          <span style={{ color: '#999' }}>—</span>
                        )}
                      </Typography>
                      <Tooltip title={row.device_id}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {shortId(row.device_id)}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {row.app_version ?? '—'}
                  </TableCell>
                  <TableCell>
                    <StatusChip row={row} />
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 280,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.operator_note ?? (
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CrashDetailDialog
        summary={activeSummary}
        detail={activeDetail}
        error={detailError}
        onClose={closeDetail}
      />
    </Stack>
  );
}

function CrashDetailDialog({
  summary,
  detail,
  error,
  onClose,
}: {
  summary: CrashReportSummary | null;
  detail: CrashReportDetail | null;
  error: string | null;
  onClose: () => void;
}) {
  if (summary === null) return null;
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
        >
          <span>Crash from {summary.tenant_name ?? summary.tenant_slug ?? 'unknown'}</span>
          <Typography variant="caption" color="text.secondary">
            {formatTs(summary.captured_at)}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={2}>
          <DetailField label="Tenant">
            {summary.tenant_name ?? '—'} ({summary.tenant_slug ?? '—'})
          </DetailField>
          <DetailField label="Device">
            <Stack>
              <span>{summary.device_label ?? '—'}</span>
              <code style={{ fontSize: '0.85em', opacity: 0.7 }}>
                {summary.device_id}
              </code>
            </Stack>
          </DetailField>
          {summary.last_session_id && (
            <DetailField label="Session active at crash">
              <code>{summary.last_session_id}</code>
            </DetailField>
          )}
          <DetailField label="App version">
            {summary.app_version ?? '—'}
          </DetailField>
          <DetailField label="Uploaded">
            {formatTs(summary.created_at)}
          </DetailField>
          {summary.operator_note && (
            <DetailField label="Operator note">
              {summary.operator_note}
            </DetailField>
          )}
          <Box>
            <Typography variant="overline" color="text.secondary">
              Stack trace
            </Typography>
            {detail === null && !error && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {detail && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  mt: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.78rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 360,
                  overflow: 'auto',
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? theme.palette.background.default
                      : '#fafafa',
                }}
              >
                {detail.stack_trace}
              </Paper>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          startIcon={<DownloadIcon />}
          onClick={() => detail && downloadCrashTxt(detail)}
          disabled={detail === null}
        >
          Download .txt
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Build a human-readable text file from the detail payload and
 * trigger a browser save via a transient blob URL. No re-fetch —
 * the dialog already has the stack trace in `detail.stack_trace`.
 *
 * The format leads with a metadata header so the operator pasting
 * this into an issue tracker, debugger, or email has the context
 * up front, then the raw stack trace below.
 *
 * Filename: `crash-<tenant_slug-or-short_id>-<short_report_id>-<captured_at>.txt`.
 * Spaces / colons in the timestamp get folded to dashes so platforms
 * with finicky filename rules (Windows) don't mangle it.
 */
function downloadCrashTxt(detail: CrashReportDetail): void {
  const lines = [
    'Crash Report',
    '============',
    `Tenant:           ${detail.tenant_name ?? '—'} (${detail.tenant_slug ?? detail.tenant_id})`,
    `Tenant ID:        ${detail.tenant_id}`,
    `Device:           ${detail.device_label ?? '—'}`,
    `Device ID:        ${detail.device_id}`,
    `App version:      ${detail.app_version ?? '—'}`,
    `Session active:   ${detail.last_session_id ?? '—'}`,
    `Captured at:      ${detail.captured_at}`,
    `Uploaded at:      ${detail.created_at}`,
    `Downloaded at:    ${detail.downloaded_at ?? '—'}`,
    `Report ID:        ${detail.id}`,
  ];
  if (detail.operator_note) {
    lines.push('', 'Operator note:', detail.operator_note);
  }
  lines.push('', 'Stack trace:', '------------', detail.stack_trace, '');
  const blob = new Blob([lines.join('\n')], {
    type: 'text/plain;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const tenantPart =
    detail.tenant_slug ?? detail.tenant_id.slice(0, 8);
  const reportPart = detail.id.slice(0, 8);
  const tsPart = detail.captured_at.replace(/[:.]/g, '-');
  const filename = `crash-${tenantPart}-${reportPart}-${tsPart}.txt`;
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so Safari's download pipeline has time to read the
  // blob; immediate revoke is racy on that engine.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack direction="row" spacing={2} alignItems="baseline">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 200 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        {children}
      </Typography>
    </Stack>
  );
}
